import React from "react";
import cx from "classnames";
import { Switch, Route, Redirect, useLocation } from "react-router-dom";
import PerfectScrollbar from "perfect-scrollbar";
import "perfect-scrollbar/css/perfect-scrollbar.css";

// @material-ui/core components
import { makeStyles, useTheme } from "@material-ui/core/styles";
import useMediaQuery from "@material-ui/core/useMediaQuery";

// core components
import AdminNavbar from "customComponents/Navbars/Navbar.js";
import Footer from "customComponents/webFooter.js";
import Sidebar from "customComponents/CustomSidebar";
import CookiesNotice from "components/CookiesNotice/CookiesNotice.js";

import routes from "routes.js";
import ProtectedRoute from "../ProtectedRoute";
import PrivacyRoute from "PrivacyRoute";
import { useGlobal } from "GlobalContext";

import styles from "assets/jss/material-dashboard-pro-react/layouts/adminStyle.js";

/**
 * Instancia compartida de PerfectScrollbar para el panel principal.
 * Se destruye/crea según el viewport y el contenido.
 */
let ps = null;

/**
 * Breakpoint operativo del proyecto:
 * >= 960px => desktop
 * < 960px  => móvil / tablet pequeña
 */
const DESKTOP_BREAKPOINT = 960;

/**
 * Marca fija del top bar móvil.
 * Se mantiene explícita aquí para evitar volver al texto dinámico de ruta.
 */
const MOBILE_BRAND_TEXT = "ePalSafer";

/**
 * Alturas aproximadas del banner de cookies.
 * Se usan para reservar espacio al final del contenido
 * y evitar que tape el último bloque visible.
 */
const COOKIE_NOTICE_HEIGHT_DESKTOP = 96;
const COOKIE_NOTICE_HEIGHT_MOBILE = 152;
const COOKIE_NOTICE_EXTRA_GAP = 12;

const useStyles = makeStyles(styles);

export default function Dashboard(props) {
  const { ...rest } = props;
  const classes = useStyles();
  const location = useLocation();
  const theme = useTheme();
  const { sharedValue } = useGlobal();

  /**
   * Solo mostramos el navbar superior en móvil/tablet pequeña.
   * En desktop la navegación principal es la sidebar fija.
   */
  const showMobileNavbar = useMediaQuery(theme.breakpoints.down("sm"));

  /**
   * Estado del drawer móvil.
   */
  const [mobileOpen, setMobileOpen] = React.useState(false);

  /**
   * Estado del sidebar mini en desktop.
   */
  const [miniActive, setMiniActive] = React.useState(false);

  /**
   * Estado visual para aplicar la clase que fuerza overflow hidden
   * cuando PerfectScrollbar está realmente activo.
   */
  const [hasPerfectScrollbar, setHasPerfectScrollbar] = React.useState(false);

  /**
   * Flag operativa para saber si estamos en desktop real según el breakpoint
   * del layout, no según el navbar móvil.
   */
  const [isDesktopViewport, setIsDesktopViewport] = React.useState(() => {
    if (typeof window === "undefined") return true;
    return window.innerWidth >= DESKTOP_BREAKPOINT;
  });

  /**
   * Offset dinámico del banner respecto al borde inferior del viewport.
   * Mientras no haya colisión con el footer será 0.
   * Cuando el footer sube, este valor aumenta y el banner asciende.
   */
  const [cookieNoticeBottomOffset, setCookieNoticeBottomOffset] =
    React.useState(0);

  /**
   * Altura estimada / medida del banner.
   * Se usa para reservar espacio al final del contenido.
   */
  const [cookieNoticeHeight, setCookieNoticeHeight] = React.useState(
    showMobileNavbar
      ? COOKIE_NOTICE_HEIGHT_MOBILE
      : COOKIE_NOTICE_HEIGHT_DESKTOP
  );

  /**
   * Evita mostrar el banner antes de tener una medición inicial correcta.
   * Esto elimina el efecto de “aparece al hacer scroll” y la caída rara
   * desde una posición incorrecta.
   */
  const [cookieNoticeReady, setCookieNoticeReady] = React.useState(false);

  /**
   * Estos valores se mantienen constantes.
   */
  const image = React.useMemo(
    () => require("assets/img/sidebar-2.jpg").default,
    []
  );
  const color = "blue";
  const bgColor = "black";
  const logo = React.useMemo(
    () => require("customAssets/logo-white.svg").default,
    []
  );

  /**
   * Ref estable al panel principal.
   */
  const mainPanel = React.useRef(null);

  /**
   * Ref al contenedor real del footer.
   * Nos permite medir cuándo entra en colisión con el banner.
   */
  const footerAnchorRef = React.useRef(null);

  /**
   * Ref para agrupar y cancelar cálculos con requestAnimationFrame.
   */
  const dockSyncRafRef = React.useRef(null);

  /**
   * Determina si estamos en la única ruta de mapa full-screen
   * que debe saltarse el layout estándar.
   */
  const isMapRoute = React.useMemo(() => {
    return location.pathname === "/admin/full-screen-maps";
  }, [location.pathname]);

  /**
   * El banner de cookies solo debe mostrarse si:
   * - no estamos en la ruta de mapa
   * - el usuario todavía no ha aceptado / denegado
   */
  const showCookiesNotice = React.useMemo(() => {
    return !isMapRoute && !sharedValue.cookiesAccepted;
  }, [isMapRoute, sharedValue.cookiesAccepted]);

  /**
   * Clases finales del panel principal.
   */
  const mainPanelClasses = cx(classes.mainPanel, {
    [classes.mainPanelSidebarMini]: miniActive,
    [classes.mainPanelWithPerfectScrollbar]: hasPerfectScrollbar,
  });

  /**
   * Abre/cierra el drawer móvil.
   */
  const handleDrawerToggle = () => {
    setMobileOpen((prev) => !prev);
  };

  /**
   * Se mantiene por compatibilidad con el layout actual.
   */
  const sidebarMinimize = () => {
    setMiniActive((prev) => !prev);
  };

  /**
   * Construye las rutas del Switch respetando:
   * - rutas privadas
   * - rutas condicionadas por privacidad
   * - rutas normales
   */
  const getRoutes = React.useCallback(
    (routeList) =>
      routeList.flatMap((prop, key) => {
        if (prop.collapse) {
          return getRoutes(prop.views);
        }

        if (prop.private) {
          return (
            <ProtectedRoute
              exact
              path={prop.path}
              component={prop.component}
              key={key}
            />
          );
        }

        if (prop.privacyConditional) {
          return <PrivacyRoute exact path={prop.path} key={key} />;
        }

        return (
          <Route
            exact
            path={prop.path}
            component={prop.component}
            key={key}
          />
        );
      }),
    []
  );

  /**
   * Sincroniza el comportamiento del panel principal según:
   * - si estamos en móvil o desktop
   * - si el contenido necesita scroll real
   * - si merece la pena activar PerfectScrollbar
   */
  const syncMainPanelScroll = React.useCallback(() => {
    const panel = mainPanel.current;
    if (!panel) return;

    const isDesktop = window.innerWidth >= DESKTOP_BREAKPOINT;
    const isWindows =
      typeof navigator !== "undefined" &&
      navigator.platform &&
      navigator.platform.indexOf("Win") > -1;

    if (!isDesktop || isMapRoute) {
      if (ps) {
        ps.destroy();
        ps = null;
      }

      setHasPerfectScrollbar(false);
      setIsDesktopViewport(false);
      panel.style.overflow = "visible";
      panel.style.overflowX = "hidden";
      panel.style.overflowY = "visible";
      document.body.style.overflow = "auto";
      return;
    }

    setIsDesktopViewport(true);

    /**
     * En desktop dejamos el body bloqueado para que el scroll ocurra
     * en el panel principal.
     */
    document.body.style.overflow = "hidden";

    /**
     * Fuera de Windows dejamos scroll nativo en el panel principal.
     */
    if (!isWindows) {
      if (ps) {
        ps.destroy();
        ps = null;
      }

      setHasPerfectScrollbar(false);
      panel.style.overflowX = "hidden";
      panel.style.overflowY = "auto";
      return;
    }

    /**
     * En Windows activamos PerfectScrollbar solo si el contenido
     * realmente desborda en vertical.
     */
    const needsScroll = panel.scrollHeight > panel.clientHeight;

    if (needsScroll) {
      if (!ps) {
        ps = new PerfectScrollbar(panel, {
          suppressScrollX: true,
          suppressScrollY: false,
        });
      } else {
        ps.update();
      }

      setHasPerfectScrollbar(true);
      panel.style.overflow = "hidden";
      return;
    }

    /**
     * Si ya no hace falta scroll especial, destruimos el plugin.
     */
    if (ps) {
      ps.destroy();
      ps = null;
    }

    setHasPerfectScrollbar(false);
    panel.style.overflowX = "hidden";
    panel.style.overflowY = "auto";
  }, [isMapRoute]);

  /**
   * Calcula cuánto debe subir el banner para no pisar el footer.
   *
   * Lógica:
   * - mientras el footer esté fuera del viewport inferior → offset 0
   * - cuando el footer entra en la zona visible → offset positivo exacto
   *
   * Importante:
   * - mantenemos una fórmula estable y directa
   * - no aplicamos animación aquí
   * - evitamos oscilaciones por múltiples cálculos redundantes
   */
  const syncCookieNoticeDocking = React.useCallback(() => {
    if (!showCookiesNotice || !footerAnchorRef.current) {
      setCookieNoticeBottomOffset((prev) => (prev === 0 ? prev : 0));
      return 0;
    }

    const footerRect = footerAnchorRef.current.getBoundingClientRect();
    const viewportHeight =
      window.innerHeight || document.documentElement.clientHeight || 0;

    const nextOffset =
      footerRect.top < viewportHeight
        ? Math.max(0, Math.round(viewportHeight - footerRect.top))
        : 0;

    setCookieNoticeBottomOffset((prev) =>
      prev === nextOffset ? prev : nextOffset
    );

    return nextOffset;
  }, [showCookiesNotice]);

  /**
   * Programa una sincronización del docking en el próximo frame.
   * Así agrupamos cambios y evitamos tirones.
   */
  const scheduleCookieNoticeDockingSync = React.useCallback(() => {
    if (dockSyncRafRef.current) {
      window.cancelAnimationFrame(dockSyncRafRef.current);
    }

    dockSyncRafRef.current = window.requestAnimationFrame(() => {
      syncCookieNoticeDocking();
    });
  }, [syncCookieNoticeDocking]);

  /**
   * Efecto principal del layout.
   */
  React.useEffect(() => {
    const previousBodyOverflow = document.body.style.overflow;

    const handleResize = () => {
      if (window.innerWidth >= DESKTOP_BREAKPOINT) {
        setMobileOpen(false);
      }

      syncMainPanelScroll();
      scheduleCookieNoticeDockingSync();
    };

    handleResize();
    window.addEventListener("resize", handleResize);

    return () => {
      if (ps) {
        ps.destroy();
        ps = null;
      }

      if (dockSyncRafRef.current) {
        window.cancelAnimationFrame(dockSyncRafRef.current);
        dockSyncRafRef.current = null;
      }

      document.body.style.overflow = previousBodyOverflow;
      window.removeEventListener("resize", handleResize);

      const panel = mainPanel.current;
      if (panel) {
        panel.style.overflow = "";
        panel.style.overflowX = "";
        panel.style.overflowY = "";
      }
    };
  }, [syncMainPanelScroll, scheduleCookieNoticeDockingSync]);

  /**
   * Re-sincroniza el panel al cambiar:
   * - la ruta
   * - el estado mini del sidebar
   */
  React.useEffect(() => {
    if (window.innerWidth < DESKTOP_BREAKPOINT) {
      setMobileOpen(false);
    }

    const rafId = window.requestAnimationFrame(() => {
      syncMainPanelScroll();
      scheduleCookieNoticeDockingSync();
    });

    return () => window.cancelAnimationFrame(rafId);
  }, [location.pathname, miniActive, syncMainPanelScroll, scheduleCookieNoticeDockingSync]);

  /**
   * Medición inicial del banner y del footer.
   *
   * Usamos useLayoutEffect + doble RAF para asegurar que:
   * - el footer ya está en DOM
   * - el layout ya está asentado
   * - el banner no aparece hasta tener una posición inicial correcta
   */
  React.useLayoutEffect(() => {
    if (!showCookiesNotice) {
      setCookieNoticeBottomOffset(0);
      setCookieNoticeReady(false);
      return;
    }

    setCookieNoticeReady(false);

    let raf1 = null;
    let raf2 = null;

    raf1 = window.requestAnimationFrame(() => {
      raf2 = window.requestAnimationFrame(() => {
        syncCookieNoticeDocking();
        setCookieNoticeReady(true);
      });
    });

    return () => {
      if (raf1) window.cancelAnimationFrame(raf1);
      if (raf2) window.cancelAnimationFrame(raf2);
    };
  }, [
    showCookiesNotice,
    location.pathname,
    miniActive,
    isDesktopViewport,
    syncCookieNoticeDocking,
  ]);

  /**
   * Si ya no debemos mostrar navbar móvil, cerramos también el drawer móvil.
   */
  React.useEffect(() => {
    if (!showMobileNavbar) {
      setMobileOpen(false);
    }
  }, [showMobileNavbar]);

  /**
   * Mantiene actualizada la altura estimada del banner
   * cuando cambia el breakpoint.
   */
  React.useEffect(() => {
    setCookieNoticeHeight(
      showMobileNavbar
        ? COOKIE_NOTICE_HEIGHT_MOBILE
        : COOKIE_NOTICE_HEIGHT_DESKTOP
    );
  }, [showMobileNavbar]);

  /**
   * Escucha el scroll del contenedor correcto:
   * - desktop (>= 960px) → scroll del mainPanel
   * - móvil / tablet pequeña → scroll del documento (window)
   *
   * Además observa cambios de tamaño del panel y del footer
   * para mantener estable el acoplamiento al footer.
   */
  React.useEffect(() => {
    if (!showCookiesNotice) {
      setCookieNoticeBottomOffset(0);
      return;
    }

    let resizeObserver = null;
    let rafId = null;

    const runSync = () => {
      if (rafId) {
        window.cancelAnimationFrame(rafId);
      }

      rafId = window.requestAnimationFrame(() => {
        syncCookieNoticeDocking();
      });
    };

    const panel = mainPanel.current;
    const footer = footerAnchorRef.current;

    const usePanelScroll = isDesktopViewport && panel;

    runSync();

    window.addEventListener("resize", runSync);

    if (usePanelScroll) {
      panel.addEventListener("scroll", runSync, { passive: true });
    } else {
      window.addEventListener("scroll", runSync, { passive: true });
    }

    if (typeof ResizeObserver !== "undefined") {
      resizeObserver = new ResizeObserver(() => {
        runSync();
      });

      if (panel) {
        resizeObserver.observe(panel);
      }

      if (footer) {
        resizeObserver.observe(footer);
      }
    }

    return () => {
      if (rafId) {
        window.cancelAnimationFrame(rafId);
      }

      window.removeEventListener("resize", runSync);

      if (usePanelScroll) {
        panel.removeEventListener("scroll", runSync);
      } else {
        window.removeEventListener("scroll", runSync);
      }

      if (resizeObserver) {
        resizeObserver.disconnect();
      }
    };
  }, [showCookiesNotice, isDesktopViewport, syncCookieNoticeDocking]);

  /**
   * Estilo inline para reservar un pequeño espacio al final del contenido
   * cuando el banner está visible.
   *
   * Así evitamos que el banner tape el último bloque útil de la página.
   */
  const contentContainerStyle = showCookiesNotice
    ? { paddingBottom: cookieNoticeHeight + COOKIE_NOTICE_EXTRA_GAP }
    : undefined;

  return (
    <div className={classes.wrapper}>
      <Sidebar
        routes={routes}
        logoText=""
        logo={logo}
        image={image}
        handleDrawerToggle={handleDrawerToggle}
        open={mobileOpen}
        color={color}
        bgColor={bgColor}
        miniActive={miniActive}
        {...rest}
      />

      <div className={mainPanelClasses} ref={mainPanel}>
        {showMobileNavbar ? (
          <AdminNavbar
            sidebarMinimize={sidebarMinimize}
            miniActive={miniActive}
            brandText={MOBILE_BRAND_TEXT}
            handleDrawerToggle={handleDrawerToggle}
            {...rest}
          />
        ) : null}

        {isMapRoute ? (
          <div className={classes.map}>
            <Switch>
              {getRoutes(routes)}
              <Redirect from="/admin" to="/admin/dashboard" />
            </Switch>
          </div>
        ) : (
          <div className={classes.content}>
            <div className={classes.container} style={contentContainerStyle}>
              <Switch>
                {getRoutes(routes)}
                <Redirect from="/admin" to="/admin/dashboard" />
              </Switch>
            </div>
          </div>
        )}

        {!isMapRoute ? (
          <>
            <div ref={footerAnchorRef}>
              <Footer fluid />
            </div>

            {showCookiesNotice ? (
              <CookiesNotice
                bottomOffset={cookieNoticeBottomOffset}
                isDockedToFooter={cookieNoticeBottomOffset > 0}
                isReady={cookieNoticeReady}
                onHeightChange={setCookieNoticeHeight}
              />
            ) : null}
          </>
        ) : null}
      </div>
    </div>
  );
}